import { getBlockUiKey, type PageBlock } from '@/types/block';

export const COLUMN_SPAN_DEFAULT = 12;

const COLUMN_SPAN_MIN = 1;

const sortBlocks = (
  blocks: PageBlock[],
  getTieBreakerId: (block: PageBlock) => string,
): PageBlock[] => {
  return [...blocks].sort((first, second) => {
    if (first.position !== second.position) {
      return first.position - second.position;
    }

    return getTieBreakerId(first).localeCompare(getTieBreakerId(second));
  });
};

export const sortBlocksByPosition = (blocks: PageBlock[]): PageBlock[] => {
  return sortBlocks(blocks, (block) => block.id);
};

export const sortBlocksByStablePosition = (blocks: PageBlock[]): PageBlock[] => {
  return sortBlocks(blocks, getBlockUiKey);
};

export function buildBlockTree(blocks: PageBlock[]): {
  topLevel: PageBlock[];
  childrenByParent: Map<string, PageBlock[]>;
} {
  const blockIds = new Set(blocks.map((block) => block.id));
  const childrenByParent = new Map<string, PageBlock[]>();
  const topLevel: PageBlock[] = [];

  blocks.forEach((block) => {
    const parentId = block.parentBlockId;
    if (!parentId || !blockIds.has(parentId)) {
      topLevel.push(block);
      return;
    }

    const children = childrenByParent.get(parentId) || [];
    children.push(block);
    childrenByParent.set(parentId, children);
  });

  return {
    topLevel: sortBlocksByPosition(topLevel),
    childrenByParent: new Map(
      Array.from(childrenByParent.entries()).map(([parentId, children]) => [
        parentId,
        sortBlocksByPosition(children),
      ]),
    ),
  };
}

export function buildStableBlockTree(blocks: PageBlock[]): {
  topLevel: PageBlock[];
  childrenByParent: Map<string, PageBlock[]>;
} {
  // Editor-only tree keyed by UI keys so autosave ID remaps cannot invalidate DnD/selection.
  const uiKeyByPersistedId = new Map(blocks.map((block) => [block.id, getBlockUiKey(block)]));
  const uiKeys = new Set(Array.from(uiKeyByPersistedId.values()));
  const childrenByParent = new Map<string, PageBlock[]>();
  const topLevel: PageBlock[] = [];

  blocks.forEach((block) => {
    const parentId = block.parentBlockId ? uiKeyByPersistedId.get(block.parentBlockId) : null;
    if (!parentId || !uiKeys.has(parentId)) {
      topLevel.push(block);
      return;
    }

    const children = childrenByParent.get(parentId) || [];
    children.push(block);
    childrenByParent.set(parentId, children);
  });

  return {
    topLevel: sortBlocksByStablePosition(topLevel),
    childrenByParent: new Map(
      Array.from(childrenByParent.entries()).map(([parentId, children]) => [
        parentId,
        sortBlocksByStablePosition(children),
      ]),
    ),
  };
}

export function buildSubtreeBlocks(
  rootBlock: PageBlock,
  childrenByParent: Map<string, PageBlock[]>,
): PageBlock[] {
  const result: PageBlock[] = [rootBlock];
  const rootUiKey = getBlockUiKey(rootBlock);
  const stack = [rootUiKey];
  const visited = new Set([rootUiKey]);

  while (stack.length > 0) {
    const parentId = stack.pop();
    if (!parentId) continue;
    const children = childrenByParent.get(parentId) || [];
    for (const child of children) {
      const childUiKey = getBlockUiKey(child);
      if (visited.has(childUiKey)) {
        continue;
      }

      visited.add(childUiKey);
      result.push(child);
      stack.push(childUiKey);
    }
  }

  return result;
}

export function collectDescendantIds(
  blockId: string,
  childrenByParent: Map<string, PageBlock[]>,
): Set<string> {
  const visited = new Set<string>();
  const stack = [blockId];

  while (stack.length > 0) {
    const currentId = stack.pop();
    if (!currentId) {
      continue;
    }

    const children = childrenByParent.get(currentId) || [];
    children.forEach((child) => {
      if (visited.has(child.id)) {
        return;
      }

      visited.add(child.id);
      stack.push(child.id);
    });
  }

  return visited;
}

export function collectDescendantStableIds(
  blockUiKey: string,
  childrenByParent: Map<string, PageBlock[]>,
): Set<string> {
  const visited = new Set<string>();
  const stack = [blockUiKey];

  while (stack.length > 0) {
    const currentId = stack.pop();
    if (!currentId) {
      continue;
    }

    const children = childrenByParent.get(currentId) || [];
    children.forEach((child) => {
      const childUiKey = getBlockUiKey(child);
      if (visited.has(childUiKey)) {
        return;
      }

      visited.add(childUiKey);
      stack.push(childUiKey);
    });
  }

  return visited;
}

export function normalizeBlocksForEditor(blocks: PageBlock[] | undefined): PageBlock[] | undefined {
  if (!blocks) {
    return blocks;
  }

  let normalizedBlocks: PageBlock[] | null = null;
  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    if (block.uiKey) {
      if (normalizedBlocks) {
        normalizedBlocks.push(block);
      }
      continue;
    }

    if (!normalizedBlocks) {
      normalizedBlocks = blocks.slice(0, index);
    }

    normalizedBlocks.push({
      ...block,
      uiKey: getBlockUiKey(block),
    });
  }

  return normalizedBlocks ?? blocks;
}

export function orderBlocksForPayload(blocks: PageBlock[]): PageBlock[] {
  const { topLevel, childrenByParent } = buildBlockTree(blocks);
  const ordered: PageBlock[] = [];

  const visit = (block: PageBlock) => {
    ordered.push(block);
    const children = childrenByParent.get(block.id) || [];
    children.forEach((child) => {
      visit(child);
    });
  };

  topLevel.forEach((block) => {
    visit(block);
  });

  const includedIds = new Set(ordered.map((block) => block.id));
  const orphaned = blocks.filter((block) => !includedIds.has(block.id));

  if (orphaned.length > 0) {
    return [...ordered, ...sortBlocksByPosition(orphaned)];
  }

  return ordered;
}

export function clampColumnSpan(columnSpan: number): number {
  return Math.max(
    COLUMN_SPAN_MIN,
    Math.min(COLUMN_SPAN_DEFAULT, Math.trunc(columnSpan || COLUMN_SPAN_DEFAULT)),
  );
}
